import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { useTheme } from '@mui/material/styles'
import {
  ButtonBase,
  Fade,
  InputBase,
  MenuItem,
  useMediaQuery,
} from '@mui/material'
import { Actions, DownshiftProps, PropGetters } from 'downshift'
import { HandleSearch, SearchOptions } from './useHeaderAutoSuggest'
import { Search } from '@mui/icons-material'
import messages from '@utils/messages'
import BackArrowIcon from '@src/icons/BackArrowIcon'
import XIcon from '@src/icons/XIcon'
import hasTouch from '@src/styles/utils/hasTouch'
import LiquidSelect from '@src/components/LiquidSelect'
import {
  SearchOption,
  SrpExperience,
  useSearchOptions,
} from '@src/utils/searchUtils'
import {
  getSearchFocusEventValue,
  sendSearchInteractionEvent,
} from '@src/utils/analytics'
import {
  HeaderSearchAddToCart,
  HeaderSearchAddToCartProps,
} from './HeaderSearchAddToCart'
import { useCurrentUser } from '@utils/useCurrentUser'
import { AppCookies, useCookies } from '@src/utils/cookies'

const useStyles = makeStyles()((theme) => ({
  autoSuggestInputWrapper: {
    display: 'flex',
    padding: theme.spacing(2.5, 4),
    border: 'none',
    borderRadius: '5px',
    [theme.breakpoints.up('md')]: {
      border: `1px solid ${theme.palette.grey[500]}`,
      height: 48,
      padding: 0,
    },
  },
  searchInputRoot: {
    borderRadius: 5,
    paddingLeft: theme.spacing(3),
    border: `1px solid ${theme.palette.grey[500]}`,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      border: 'none',
    },
  },
  searchInput: {
    order: 2,
    height: '46px',
    paddingTop: '0',
    paddingBottom: '0',
    '&::placeholder': {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.grey[500],
      opacity: '1',
    },
    [theme.breakpoints.up('md')]: {
      '&::placeholder': {
        fontSize: theme.typography.pxToRem(14),
        opacity: '0.75 !important',
        color: 'unset',
      },
    },
    '&:focus::placeholder': {
      // Visually remove placeholder text when input is focused.
      color: 'transparent',
    },
  },
  searchButton: {
    order: 4,
    flexShrink: 0,
    height: '70%',
    width: theme.spacing(11),
    borderLeft: `1px solid ${theme.palette.grey[400]}`,
    marginLeft: theme.spacing(4),
    color: theme.palette.brand.main,
    fontSize: theme.typography.pxToRem(20),
    [theme.breakpoints.up('md')]: {
      height: '100%',
      width: theme.spacing(14),
      fontSize: theme.typography.pxToRem(26),
      color: theme.palette.common.white,
      backgroundColor: theme.palette.brand.main,
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
      border: `1px solid ${theme.palette.brand.main}`,
      boxSizing: 'content-box',
      right: '-1px',
      marginLeft: theme.spacing(3),
    },
  },
  searchIcon: {
    fontSize: theme.typography.pxToRem(20),
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(26),
    },
  },
  backButton: {
    order: 1,
    height: '100%',
    marginRight: 12,
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  hideBackButton: {
    display: 'none',
  },
  scanButton: { display: 'inline-flex', marginLeft: 12, order: 4 },
  addToCartGroup: {
    flexShrink: 0,
    order: 3,
    marginLeft: theme.spacing(3),
  },
  clearButton: {
    display: 'none',
    visibility: 'hidden',
    order: 3,
    marginTop: 2,
  },
  showClear: {
    display: 'inline-flex',
    visibility: 'visible',
  },
  headerSelectInputOverrides: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    '& .MuiSelect-select': {
      boxShadow: 'none',
      borderRight: `1px solid ${theme.palette.grey[400]}`,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      alignContent: 'center',
    },
    '& .MuiSelect-select:focus': {
      boxShadow: 'none',
    },
    '& .MuiSelect-icon': {
      top: '50% !important',
      transform: 'translateY(-50%)',
      marginTop: 0,
    },
  },
}))

interface HeaderSearchInputProps {
  getInputProps: PropGetters<SearchOptions>['getInputProps']
  openMenu: Actions<SearchOptions>['openMenu']
  closeMenu: Actions<SearchOptions>['closeMenu']
  isOpen: DownshiftProps<SearchOptions>['isOpen']
  handleSearch: HandleSearch
  inputEl: React.RefObject<HTMLInputElement>
  handleClearSelection: Actions<SearchOptions>['clearSelection']
  inputValue: DownshiftProps<SearchOptions>['inputValue']
  setFocusType: (type: any) => void
  focusType: string
  orgId: string | null | undefined
  id: string
  headerSearchAddToCartProps: HeaderSearchAddToCartProps
}

const HeaderSearchInput: React.FC<HeaderSearchInputProps> = ({
  getInputProps,
  openMenu,
  closeMenu,
  isOpen,
  handleSearch,
  inputEl,
  handleClearSelection,
  inputValue,
  setFocusType,
  focusType,
  orgId,
  id,
  headerSearchAddToCartProps,
}) => {
  const { currentUser } = useCurrentUser()
  const { classes, cx } = useStyles()
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const { onBlur } = getInputProps()
  const options: SearchOption[] = useSearchOptions()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true
  const [dropdownLabel, setDropdownLabel] = useState('products')
  const searchValue = inputEl.current?.defaultValue
  const [cookies] = useCookies([AppCookies.SrpExperience])

  const srpExperience: SrpExperience =
    cookies[AppCookies.SrpExperience] || 'new'

  const newSrpEnabled = srpExperience === 'new'

  return (
    <div className={classes.autoSuggestInputWrapper}>
      {!orgId && (
        <LiquidSelect
          className={classes.headerSelectInputOverrides}
          onChange={(e) => {
            sendSearchInteractionEvent({
              action: 'search scope change',
              component: 'drop-down menu',
              elementType: 'button',
              elementText: getSearchFocusEventValue(
                e.target.value
              ).toLowerCase(),
              detail: dropdownLabel,
            })
            setDropdownLabel(getSearchFocusEventValue(e.target.value))
            setFocusType(e.target.value)
          }}
          size="small"
          value={focusType}
          fullWidth={false}
        >
          {options.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </LiquidSelect>
      )}
      <InputBase
        {...getInputProps()}
        data-testid="primary-search-input"
        onFocus={() => openMenu()}
        onBlur={(...args: [event: React.FocusEvent<HTMLInputElement>]) => {
          if (!hasTouch) {
            onBlur && onBlur(...args)
          }
        }}
        inputRef={inputEl}
        placeholder={formatMessage(messages.HEADER_SEARCH_PLACEHOLDER)}
        classes={{
          root: classes.searchInputRoot,
          input: classes.searchInput,
        }}
        onKeyUp={(event) => {
          if (event.key === 'Enter' && isOpen) {
            closeMenu()
            handleSearch({
              focusType,
              searchValue,
              dropdownLabel,
              newSrpEnabled,
            })
          }
        }}
        inputProps={{
          spellCheck: false,
        }}
        endAdornment={
          <>
            {isOpen && (
              <ButtonBase
                className={classes.backButton}
                onClick={() => {
                  closeMenu()
                }}
              >
                <BackArrowIcon fontSize="inherit" />
              </ButtonBase>
            )}
            {headerSearchAddToCartProps.material &&
              !isDesktop &&
              (!headerSearchAddToCartProps.material.isMarketplace ||
                currentUser?.metadata.isMarketplaceCartEnabled) && (
                <Fade in>
                  <div className={classes.addToCartGroup}>
                    <HeaderSearchAddToCart {...headerSearchAddToCartProps} />
                  </div>
                </Fade>
              )}
            <ButtonBase
              className={cx(classes.clearButton, {
                [classes.showClear]: !!inputValue && inputValue !== '',
              })}
              onClick={() => handleClearSelection()}
              aria-label={formatMessage({
                id: 'CLEAR_SEARCH',
                defaultMessage: 'Clear Search',
              })}
            >
              <XIcon fontSize="small" />
            </ButtonBase>
            <ButtonBase
              data-testid={id}
              id={id}
              className={classes.searchButton}
              aria-label={formatMessage({
                id: 'SEARCH',
                defaultMessage: 'Search',
              })}
              onClick={() => {
                handleSearch({
                  focusType,
                  searchValue,
                  dropdownLabel,
                  newSrpEnabled,
                })
                closeMenu()
              }}
            >
              <Search className={classes.searchIcon} />
            </ButtonBase>
          </>
        }
      />
    </div>
  )
}

export default HeaderSearchInput
